/**
 * ConnectBankButton
 *
 * Starts the TrueLayer Auth Dialog flow for UK Open Banking.
 * Provider selection and consent collection happen inside the hosted dialog.
 */

import { useState } from 'react';
import { Building2, ChevronRight, Loader2 } from 'lucide-react';
import { initiateBankConnection, isOpenBankingEnabled } from '@/lib/open-banking';
import { useToast } from '@/hooks/use-toast';
import { AppButton, AppCard, AppCardBody, AppText } from '../../ui-foundation';

interface Props {
  userId: string;
  /** URL the bank will redirect to after auth */
  redirectUrl?: string;
}

export default function ConnectBankButton({ userId, redirectUrl }: Props) {
  const { toast } = useToast();
  const [connecting, setConnecting] = useState(false);

  if (!isOpenBankingEnabled()) {
    return (
      <AppCard borderStyle="dashed" backgroundColor="$cardBackground" opacity={0.75} alignItems="center">
        <Building2 size={22} className="mx-auto text-muted-foreground mb-2" />
        <AppText fontSize="$sm" fontWeight="700">Open Banking</AppText>
        <AppText className="mt-1 mb-3 max-w-xs text-center" fontSize="$xs" color="$colorMuted">
          Connect your UK bank account to import transactions automatically.
        </AppText>
        <AppText fontSize="$xs" color="$colorMuted" textAlign="center">
          Set <code className="bg-muted px-1 rounded">VITE_OPEN_BANKING_ENABLED=true</code> and add your TrueLayer client credentials to enable.
        </AppText>
      </AppCard>
    );
  }

  async function handleConnect() {
    setConnecting(true);
    try {
      const callbackUrl = redirectUrl ?? `${window.location.origin}/money?tab=budget`;
      const { authUrl } = await initiateBankConnection(callbackUrl, userId);
      window.location.href = authUrl;
    } catch (err) {
      toast({
        title: 'Could not start bank connection',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
      setConnecting(false);
    }
  }

  return (
    <AppCardBody>
      <AppButton
        tone="outline"
        width="100%"
        justifyContent="flex-start"
        onPress={() => void handleConnect()}
        disabled={connecting}
      >
        <Building2 size={18} className="text-teal shrink-0" />
        <span className="flex-1 text-left">
          {connecting ? 'Opening secure bank consent…' : 'Connect a bank account'}
        </span>
        {connecting ? (
          <Loader2 size={16} className="text-primary animate-spin" />
        ) : (
          <ChevronRight size={16} className="text-muted-foreground" />
        )}
      </AppButton>

      <AppText fontSize="$xs" color="$colorMuted">
        Adulting uses TrueLayer&apos;s hosted Auth Dialog for bank selection and consent. Your bank login stays with your bank.
      </AppText>
    </AppCardBody>
  );
}
